import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Save, FileText, Eye, Clock, CheckCircle, AlertCircle,
  Loader2, X, PenTool, Send
} from 'lucide-react';
import { API } from '@/App';

const CATEGORIES = ['Cahier', 'Article', 'Rapport', 'Cours', 'Publication'];

const MesArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorStatus, setAuthorStatus] = useState('none');
  const [requesting, setRequesting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'Article', type: 'article' });
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    checkAuthorStatus();
  }, []);

  const checkAuthorStatus = async () => {
    try {
      const res = await axios.get(`${API}/author-status`);
      setAuthorStatus(res.data.author_status);
      if (res.data.author_status === 'approved') fetchArticles();
      else setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/my-articles`);
      setArticles(res.data);
    } catch { }
    setLoading(false);
  };

  const requestAuthor = async () => {
    setRequesting(true);
    try {
      await axios.post(`${API}/author-request`);
      setAuthorStatus('pending');
      toast.success('Votre demande de statut auteur a été envoyée !');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setRequesting(false);
  };

  const handleSubmitArticle = async () => {
    if (!form.title.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/my-articles`, form);
      setArticles(a => [res.data, ...a]);
      setShowForm(false);
      setForm({ title: '', content: '', category: 'Article', type: 'article' });
      toast.success('Article soumis pour approbation');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const statusBadge = (status) => {
    const config = {
      pending: { label: 'En attente', icon: Clock, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
      published: { label: 'Publié', icon: CheckCircle, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      rejected: { label: 'Refusé', icon: AlertCircle, cls: 'bg-red-50 text-red-700 border-red-200' },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${c.cls}`}>
        <Icon size={10} /> {c.label}
      </span>
    );
  };

  if (loading) return <div className="flex items-center justify-center min-h-full"><Loader2 size={32} className="animate-spin text-[#35AFE4]" /></div>;

  // Not yet author - show request button
  if (authorStatus === 'none') {
    return (
      <div className="min-h-full bg-[#f8fafb] flex items-center justify-center p-8" data-testid="author-request-page">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 max-w-xl shadow-lg">
          <div className="text-center mb-6">
            <PenTool size={48} className="mx-auto text-[#35AFE4] mb-4" />
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Devenir Auteur
            </h2>
            <p className="text-sm text-gray-600">
              Partagez votre expertise avec la communauté SFOALC en publiant cours, articles scientifiques, rapports et replays.
            </p>
          </div>

          {/* Process steps */}
          <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-5 mb-6" data-testid="author-process-steps">
            <p className="text-xs font-bold text-[#1e3a5f] uppercase tracking-wider mb-3">Comment ça marche ?</p>
            <ol className="space-y-3 text-sm">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1e3a5f] text-white text-xs font-bold flex items-center justify-center">1</span>
                <div>
                  <p className="font-semibold text-gray-900">Demandez le statut auteur</p>
                  <p className="text-xs text-gray-600 mt-0.5">Cliquez sur "Proposer d'être auteur" ci-dessous. Votre demande sera transmise à l'administration SFOALC.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1e3a5f] text-white text-xs font-bold flex items-center justify-center">2</span>
                <div>
                  <p className="font-semibold text-gray-900">Validation par l'admin SFOALC</p>
                  <p className="text-xs text-gray-600 mt-0.5">L'équipe vérifie votre profil et approuve votre statut. Vous recevrez une notification dès l'approbation.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#1e3a5f] text-white text-xs font-bold flex items-center justify-center">3</span>
                <div>
                  <p className="font-semibold text-gray-900">Rédigez et soumettez vos contenus</p>
                  <p className="text-xs text-gray-600 mt-0.5">Une fois approuvé, vous pouvez rédiger des articles, cours, rapports et replays. Chaque contenu est validé par l'admin avant publication.</p>
                </div>
              </li>
            </ol>
          </div>

          <div className="flex justify-center">
            <button onClick={requestAuthor} disabled={requesting}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1e3a5f] to-[#35AFE4] text-white text-sm font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
              data-testid="request-author-btn">
              {requesting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Proposer d'être auteur
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Pending approval
  if (authorStatus === 'pending') {
    return (
      <div className="min-h-full bg-[#f8fafb] flex items-center justify-center p-8" data-testid="author-pending-page">
        <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center max-w-md shadow-lg">
          <Clock size={48} className="mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Demande en cours de traitement
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Votre demande de statut auteur est actuellement examinée par l'administration SFOALC.
          </p>
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-left text-xs text-amber-900">
            <p className="font-bold mb-2 flex items-center gap-1.5"><AlertCircle size={13}/> Et après ?</p>
            <p>Vous recevrez une notification dès que votre demande aura été approuvée. Vous pourrez alors rédiger et soumettre vos premiers contenus, qui seront eux aussi validés par l'admin avant publication.</p>
          </div>
        </div>
      </div>
    );
  }

  // Approved author - show articles
  return (
    <div className="min-h-full bg-[#f8fafb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="mes-articles-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Mes Articles
            </h1>
            <p className="text-sm text-gray-500 mt-1">Articles soumis et publiés par vous</p>
          </div>
          <button onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#1e3a5f] to-[#35AFE4] text-white text-sm font-bold rounded-xl hover:shadow-lg transition-all"
            data-testid="new-article-btn">
            <Plus size={16} /> Rédiger un article
          </button>
        </div>

        {/* Info banner — submission process */}
        <div className="flex items-start gap-3 p-4 bg-blue-50/60 border border-blue-100 rounded-2xl mb-6" data-testid="author-info-banner">
          <FileText size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">Comment publier un contenu ?</p>
            <p className="text-blue-700 mt-0.5 text-xs leading-relaxed">
              Cliquez sur "Rédiger un article" pour créer un cours, un article scientifique, un rapport ou un replay. Une fois soumis, votre contenu passe en statut <strong>"En attente"</strong> : l'admin SFOALC le relit et le publie. Vous serez notifié de la décision (publication ou retour pour modifications).
            </p>
          </div>
        </div>

        {/* Article Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-6 mb-6" data-testid="article-form">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-[#1e3a5f]">Nouvel article</h2>
              <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Titre *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                  placeholder="Titre de l'article" data-testid="input-article-title" />
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Catégorie</label>
                <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none"
                  data-testid="select-article-category">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Contenu</label>
                <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  rows={10}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none resize-none"
                  placeholder="Rédigez votre article ici..." data-testid="input-article-content" />
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowForm(false)} className="px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-xl">Annuler</button>
                <button onClick={handleSubmitArticle} disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#1e3a5f] to-[#35AFE4] text-white text-sm font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                  data-testid="submit-article-btn">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  Soumettre pour approbation
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Articles List */}
        {articles.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
            <FileText size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 mb-4">Aucun article rédigé</p>
            <button onClick={() => setShowForm(true)} className="text-sm text-[#35AFE4] font-semibold hover:underline">
              Rédiger mon premier article
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {articles.map((a, i) => (
              <div key={a.id} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm" data-testid={`article-card-${i}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-bold text-[#1e3a5f]">{a.title}</h3>
                      {statusBadge(a.status || (a.published ? 'published' : 'pending'))}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span>{a.category || a.type}</span>
                      {a.created_at && <span>{new Date(a.created_at).toLocaleDateString('fr-FR')}</span>}
                      <span className="flex items-center gap-1"><Eye size={12} /> {a.views || 0} vue{(a.views || 0) > 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MesArticlesPage;
